import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Controller, useForm } from 'react-hook-form';
import { TxKeyPath } from '@i18n';
import { useAppTranslation } from '@hooks';
import { findKeyByValue } from '@utils/find-key';
import { RoleUserType, UserType } from '@definitions/states';
import { UploadChangeParam } from 'antd/es/upload/interface';
import { UserDataType } from '@pages/types';
import { CreateUserPayloadType } from '@api/user';
import { VerticalFormItem } from '@components/display';
import {
  TextInput,
  SelectOption,
  SelectInput,
  colors,
  Avatar,
  DropInput,
  Form,
} from 'common-components';

export type FormUserDataType = {
  createUser: CreateUserPayloadType & {
    idUser?: string
  }
  file: File
};

const errorTextColor = css`
  padding-top: 5px;
  color: red !important;
`;

type FieldsType = {
  name: 'lastName' | 'firstName' | 'phone' | 'email' | 'role' | 'type'
  defaultValue?: any
  errorMessage: string
  label: string
  placeholder?: string
  type: 'input' | 'select'
  rules: {
    required?: boolean
    pattern?: RegExp
  }
  selectOptions?: {
    label: string
    value: string
  }[]
};
const fields1 = (translate: (key: TxKeyPath) => string): FieldsType[] => [
  {
    name: 'lastName',
    errorMessage: translate('createUser.lastName.errorMessage'),
    label: translate('createUser.lastName.label'),
    placeholder: translate('createUser.lastName.placeholder'),
    type: 'input',
    rules: { required: true },
  },
  {
    name: 'firstName',
    errorMessage: translate('createUser.firstName.errorMessage'),
    label: translate('createUser.firstName.label'),
    placeholder: translate('createUser.firstName.placeholder'),
    type: 'input',
    rules: { required: true },
  },
];
const fields2 = (
  translate: (key: TxKeyPath) => string,
  roles: FormUserRoleType[],
): FieldsType[] => [
  {
    name: 'phone',
    errorMessage: translate('createUser.phone.errorMessage'),
    label: translate('createUser.phone.label'),
    placeholder: translate('createUser.phone.placeholder'),
    type: 'input',
    rules: { required: true, pattern: /^\d{10}$/ },
  },
  {
    name: 'email',
    errorMessage: translate('createUser.email.errorMessage'),
    label: translate('createUser.email.label'),
    placeholder: translate('createUser.email.placeholder'),
    type: 'input',
    rules: { required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ },
  },
  {
    name: 'role',
    errorMessage: translate('createUser.role.errorMessage'),
    label: translate('createUser.role.label'),
    type: 'select',
    rules: { required: true },
    selectOptions: roles.map((role) => ({
      label: translate(`createUser.role.selectOptions.${role}`),
      value: role,
    })),
  },
];

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 20px;
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 20px;
`;

type FormUserRoleType = Exclude<RoleUserType, 'superAdmin'>;

export type FormUserPropsType = {
  user?: Pick<
    UserDataType,
    'lastName' | 'firstName' | 'phone' | 'email' | 'type' | 'urlAvatar' | 'idUser'
  >
  roles: FormUserRoleType[]
  mode?: 'edit' | 'create'
  onChangeForm: ({ createUser, file }: FormUserDataType) => void
  isFormValid: (validation: boolean) => void
};

type FormType = FormUserDataType['createUser'] & {
  role: RoleUserType
};

const FormUser = ({ user, roles, onChangeForm, isFormValid, mode = 'create' }: FormUserPropsType) => {
  const [avatarSrc, setAvatarSrc] = useState<string>(user?.urlAvatar);
  const file = useRef<File>(undefined);
  const isSubmitted = useRef<boolean>(false);

  const { translate } = useAppTranslation();
  const {
    formState: { errors, isDirty, touchedFields },
    trigger,
    control,
    watch,
    getValues,
  } = useForm<FormType>({
    values: {
      email: user?.email,
      type: user?.type,
      firstName: user?.firstName,
      lastName: user?.lastName,
      role: UserType[user?.type],
      phone: user?.phone,
    },
  });

  const onUpload = async (ev: UploadChangeParam) => {
    const createdFile = ev.fileList[0]?.originFileObj;
    file.current = createdFile;
    isSubmitted.current = false;
    if (createdFile) {
      const src = URL.createObjectURL(createdFile);
      setAvatarSrc(src);
    }
  };

  const onChangeValues = () => {
    if (!Object.keys(errors).length && isDirty && !isSubmitted.current) {
      const values = getValues();
      const formData: FormUserDataType = {
        createUser: {
          idUser: user?.idUser,
          firstName: values.firstName,
          email: values.email,
          phone: values.phone,
          lastName: values.lastName,
          type: values.role && findKeyByValue(UserType, values.role),
        },
        file: file.current,
      };
      onChangeForm(formData);
      isSubmitted.current = true;
    }
  };

  useEffect(() => {
    const timeout = setTimeout(onChangeValues, 800);
    return () => clearTimeout(timeout);
  }, [Object.keys(errors).length, Object.values(getValues()), file.current]);

  useEffect(() => {
    // trigger form validation on init component
    trigger();
  }, []);

  useEffect(() => {
    isFormValid(!Object.keys(errors).length && isDirty);
  }, [Object.keys(errors).length, isDirty]);

  useEffect(() => {
    const subscription = watch(() => {
      // trigger form validation
      trigger();
      isSubmitted.current = false;
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Form>
      <StyledContainer>
        <ImageContainer>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 20px;
              flex: 1;
            `}
          >
            {fields1(translate).map((item) => {
              return (
                <VerticalFormItem key={item.name} label={item.label}>
                  <Controller
                    name={item.name}
                    control={control}
                    rules={item.rules}
                    defaultValue={item.defaultValue}
                    render={({ field }) => <TextInput placeholder={item.placeholder} {...field} />}
                  />
                  {errors[item.name] && touchedFields[item.name] && (
                    <div css={errorTextColor}>{item.errorMessage}</div>
                  )}
                </VerticalFormItem>
              );
            })}
          </div>
          <AvatarContainer>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1.5px solid ${colors.gray200};
                border-radius: 200px;
                backgroundcolor: ${colors.gray500};
              `}
            >
              <Avatar
                css={css`
                  width: 96px !important;
                  height: 96px !important;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
                src={avatarSrc}
                size="lg"
              />
            </div>
            <DropInput maxCount={1} onUpload={onUpload} onlyIcon />
          </AvatarContainer>
        </ImageContainer>
        {fields2(translate, roles).map((item) => {
          if (item.type === 'input') {
            return (
              <VerticalFormItem key={item.name} label={item.label}>
                <Controller
                  name={item.name}
                  control={control}
                  rules={item.rules}
                  defaultValue={item.defaultValue}
                  render={({ field }) => (
                    <TextInput
                      placeholder={item.placeholder}
                      disabled={item.name === 'email' && mode === 'edit'}
                      {...field}
                    />
                  )}
                />
                {errors[item.name] && touchedFields[item.name] && (
                  <div css={errorTextColor}>{item.errorMessage}</div>
                )}
              </VerticalFormItem>
            );
          }
          return (
            <VerticalFormItem key={item.name} label={item.label}>
              <Controller
                name={item.name}
                control={control}
                rules={item.rules}
                defaultValue={item.defaultValue}
                render={({ field }) => (
                  <SelectInput {...field}>
                    {item.selectOptions.map((option) => (
                      <SelectOption key={option.value} value={option.value}>{option.label}</SelectOption>
                    ))}
                  </SelectInput>
                )}
              />
              {errors[item.name] && touchedFields[item.name] && (
                <div css={errorTextColor}>{item.errorMessage}</div>
              )}
            </VerticalFormItem>
          );
        })}
      </StyledContainer>
    </Form>
  );
};

export default FormUser;
